import { useEffect, useMemo } from 'react';
import { Text } from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import useGetAllUsersList from '@hooks/useGetAllUsersList';
import DualListSelectDialog, { type DualListItem } from '@components/DualListSelectDialog';

import type { TeamType } from '../types';
import useGetTeamMembers from '../hooks/useGetTeamMembers';

interface Props {
  team: TeamType;
  handleClose: () => void;
  afterSubmit: () => void;
}

const UpdateMembersDialog = ({ team, handleClose, afterSubmit }: Props) => {
  const { getAllUsers, users, loading: allUsersLoading } = useGetAllUsersList();
  const { getTeamMembers, members, loading: teamMembersLoading } = useGetTeamMembers();

  useEffect(() => {
    getAllUsers();
    getTeamMembers(team.id);
  }, [getAllUsers, getTeamMembers, team]);

  const items = useMemo<DualListItem[]>(
    () => users.map((u) => ({ id: u.id, label: u.name })),
    [users]
  );

  const initialSelectedIds = useMemo(
    () => members.map((m) => m.id),
    [members]
  );

  const handleSave = async (added: string[], removed: string[]) => {
    try {
      const response = await api.patch(`/teams/${team.id}/users`, {
        add: added,
        remove: removed,
      });

      if (response?.status === 200) {
        toast.success('Membros da equipe atualizados com sucesso.');
        afterSubmit();
        handleClose();
      }
    } catch (error) {
      toast.error('Erro ao atualizar os membros da equipe. Verifique os dados e tente novamente.');
      datadogRum.addError(new Error('Fail to update team members'), {
        error,
        teamId: team.id,
        added,
        removed,
      });
      throw error;
    }
  };

  return (
    <DualListSelectDialog
      onClose={handleClose}
      title="Gerenciar membros"
      subtitle={
        <Text color="fg.muted">
          Equipe <Text as="span" fontWeight="medium">{team.name}</Text>
        </Text>
      }
      items={items}
      initialSelectedIds={initialSelectedIds}
      onSave={handleSave}
      loading={allUsersLoading || teamMembersLoading}
      labels={{
        defaultGroup: 'Membros',
        availableSubtitle: 'Selecione um ou vários membros',
        selectedSubtitle: 'Remova rapidamente o que não pertence a essa equipe',
        emptyAvailableTitle: 'Nenhum membro encontrado',
        emptyAvailableDescription: 'Tente outro termo de busca ou revise os filtros aplicados.',
        emptySelectedTitle: 'Nenhum membro selecionado',
        emptySelectedDescription: 'Escolha membros da coluna à esquerda para adicioná-los à equipe.',
      }}
    />
  );
};

export default UpdateMembersDialog;
